import moment from "moment";
import "moment/locale/es";
import { Metric } from "../../../cores/analytics/interface/api/get-insights";
import { FacebookPagePost } from "../../../cores/analytics/interface/api/get-facebook-posts";
import {
  FacebookAnalyticsSnapshot,
  FacebookAudienceSegment,
  FacebookPostMetric,
  FacebookStatCard,
  FacebookTrendPoint,
} from "./facebookAnalyticsMock";

const pickMetric = (
  entities: Record<string, Metric>,
  names: string[]
): Metric | undefined => {
  for (const name of names) {
    const m = entities[name];
    if (
      m &&
      ((m.values && m.values.length > 0) ||
        typeof m.total?.value === "number")
    ) {
      return m;
    }
  }
  return undefined;
};

const totalMetricValue = (m?: Metric): number => {
  if (!m) return 0;
  if (typeof m.total?.value === "number") return m.total.value;
  if (m.values?.length) {
    return m.values.reduce((acc, v) => acc + (Number(v.value) || 0), 0);
  }
  return 0;
};

const dateKey = (label: string): string | null => {
  if (!label?.trim()) return null;
  const iso = moment(label, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format("YYYY-MM-DD");
  const formats = [
    "MM/DD/YY",
    "MM/DD/YYYY",
    "DD/MM/YY",
    "DD/MM/YYYY",
    "MMMM D, YYYY",
    "dddd, MMMM Do YYYY",
  ];
  for (const fmt of formats) {
    const m = moment(label, fmt, true);
    if (m.isValid()) return m.format("YYYY-MM-DD");
  }
  const loose = moment(label.replace(/(\d+)(st|nd|rd|th)\b/gi, "$1"));
  return loose.isValid() ? loose.format("YYYY-MM-DD") : null;
};

const sumMetricInRange = (
  m: Metric | undefined,
  since?: string,
  until?: string
): number => {
  if (!m?.values?.length) return totalMetricValue(m);
  const sinceM = since
    ? moment(since, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true)
    : null;
  const untilM = until
    ? moment(until, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true)
    : null;
  if (!sinceM?.isValid() || !untilM?.isValid()) {
    return totalMetricValue(m);
  }
  const start = sinceM.startOf("day");
  const end = untilM.endOf("day");
  let sum = 0;
  for (const { label, value } of m.values) {
    const k = dateKey(label);
    if (!k) continue;
    const d = moment(k, "YYYY-MM-DD");
    if (d.isSameOrAfter(start) && d.isSameOrBefore(end)) {
      sum += Number(value) || 0;
    }
  }
  return sum;
};

const regionNameEs = (code: string): string => {
  const c = code.trim().toUpperCase();
  if (c.length !== 2) return code.trim();
  try {
    return new Intl.DisplayNames(["es"], { type: "region" }).of(c) ?? code;
  } catch {
    return code;
  }
};

const mapCountries = (
  m: Metric | undefined,
  fallback: FacebookAudienceSegment[]
): FacebookAudienceSegment[] => {
  if (!m?.values?.length) return fallback;
  const sorted = [...m.values]
    .map((v) => ({ label: regionNameEs(v.label), value: Number(v.value) || 0 }))
    .filter((x) => x.value > 0)
    .sort((a, b) => b.value - a.value);
  if (!sorted.length) return fallback;
  const badges = ["Principal", "Secundario"];
  return sorted.slice(0, 8).map((row, i) => ({
    label: row.label,
    value: badges[i] ?? row.value,
    detail: badges[i] ? undefined : undefined,
  }));
};

const mapCities = (
  m: Metric | undefined,
  fallback: FacebookAudienceSegment[]
): FacebookAudienceSegment[] => {
  if (!m?.values?.length) return fallback;
  const sum =
    m.values.reduce((a, v) => a + (Number(v.value) || 0), 0) || 1;
  return m.values
    .map((v) => ({
      label: v.label.trim(),
      pct: ((Number(v.value) || 0) * 100) / sum,
    }))
    .filter((x) => x.pct > 0)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 12)
    .map((row) => ({
      label: row.label,
      value: Math.round(row.pct * 100) / 100,
      detail: "%",
    }));
};

const hasLifetimeDemographics = (entities: Record<string, Metric>) =>
  Boolean(
    pickMetric(entities, ["page_fans_country", "page_follows_country"])
      ?.values?.length ||
      pickMetric(entities, ["page_fans_city", "page_follows_city"])?.values
        ?.length
  );

const hasDailyClicks = (entities: Record<string, Metric>) =>
  Boolean(
    pickMetric(entities, ["page_total_actions"]) ||
      pickMetric(entities, ["page_views_total"]) ||
      pickMetric(entities, ["page_impressions", "page_media_view"])
  );

const parseRangeMoment = (raw: string) =>
  moment(raw, ["MM/DD/YY", "MM/DD/YYYY", "YYYY-MM-DD"], true);

const formatAxisLabelEs = (key: string): string =>
  moment(key, "YYYY-MM-DD").locale("es").format("D MMM");

const daysInclusive = (since: string, until: string): number => {
  const start = parseRangeMoment(since);
  const end = parseRangeMoment(until);
  if (!start.isValid() || !end.isValid()) return 1;
  return Math.max(1, end.diff(start, "days") + 1);
};

const compareVsPrevious = (current: number, previous: number) => {
  const safeCurrent = Number(current) || 0;
  const safePrev = Number(previous) || 0;
  if (safeCurrent > safePrev) return { direction: "up" as const };
  if (safeCurrent < safePrev) return { direction: "down" as const };
  return { direction: "flat" as const };
};

const withVariation = (
  item: FacebookStatCard,
  current: number,
  previous: number
): FacebookStatCard => {
  const { direction } = compareVsPrevious(current, previous);
  return { ...item, value: current, direction };
};

const dashOrNumber = (n: number): number | string => (n > 0 ? n : "-");

const valuesToDayMap = (m?: Metric): Map<string, number> => {
  const map = new Map<string, number>();
  if (!m?.values) return map;
  for (const { label, value } of m.values) {
    const k = dateKey(label);
    if (k) map.set(k, Number(value) || 0);
  }
  return map;
};

const sortedMetricPointsInRange = (
  m: Metric | undefined,
  since: string,
  until: string
): { k: string; v: number }[] => {
  if (!m?.values?.length) return [];
  const start = parseRangeMoment(since).startOf("day");
  const end = parseRangeMoment(until).endOf("day");
  const pts = m.values
    .map((v) => {
      const k = dateKey(v.label);
      return k ? { k, v: Number(v.value) || 0 } : null;
    })
    .filter(Boolean) as { k: string; v: number }[];
  const filtered = pts.filter(({ k }) => {
    const d = moment(k, "YYYY-MM-DD");
    return d.isValid() && d.isBetween(start, end, undefined, "[]");
  });
  filtered.sort((a, b) => moment(a.k).valueOf() - moment(b.k).valueOf());
  return filtered;
};

const lastMetricValueInRange = (m: Metric | undefined, since: string, until: string) => {
  const pts = sortedMetricPointsInRange(m, since, until);
  if (pts.length) return pts[pts.length - 1].v;
  return totalMetricValue(m);
};

const hasOverviewInsights = (entities: Record<string, Metric>) =>
  Boolean(
    pickMetric(entities, ["page_fans", "page_follows"]) ||
      pickMetric(entities, ["page_impressions", "page_media_view"]) ||
      pickMetric(entities, ["page_views_total"]) ||
      pickMetric(entities, ["page_post_engagements"])
  );

const buildOverviewTrend = (
  entities: Record<string, Metric>,
  since: string,
  until: string,
  fallback: FacebookTrendPoint[]
): FacebookTrendPoint[] => {
  const fans = pickMetric(entities, ["page_fans", "page_follows"]);
  const views = pickMetric(entities, ["page_impressions", "page_media_view"]);
  const fansMap = valuesToDayMap(fans);
  const viewsMap = valuesToDayMap(views);

  const start = parseRangeMoment(since).startOf("day");
  const end = parseRangeMoment(until).startOf("day");
  if (!start.isValid() || !end.isValid()) return fallback;
  if (!fansMap.size && !viewsMap.size) return fallback;

  const trend: FacebookTrendPoint[] = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    trend.push({
      label: formatAxisLabelEs(key),
      followers: fansMap.get(key) ?? 0,
      views: viewsMap.get(key) ?? 0,
      pageViews: 0,
      posts: 0,
      reactions: 0,
      clicks: 0,
    });
    cursor.add(1, "day");
  }
  return trend.length ? trend : fallback;
};

export interface BuildFacebookOverviewInput {
  entities: Record<string, Metric>;
  previousEntities?: Record<string, Metric>;
  mock: FacebookAnalyticsSnapshot;
  since: string;
  until: string;
  previousSince?: string;
  previousUntil?: string;
  postsInPeriod: number;
  previousPostsInPeriod?: number;
  postsInPeriodRows?: FacebookPagePost[];
  previousPostsRows?: FacebookPagePost[];
}

export function buildFacebookOverviewFromInsights(
  input: BuildFacebookOverviewInput
): Pick<
  FacebookAnalyticsSnapshot,
  "growth" | "followerBalance" | "viewedPosts"
> {
  const {
    entities,
    previousEntities = {},
    mock,
    since,
    until,
    previousSince = since,
    previousUntil = until,
    postsInPeriod,
    previousPostsInPeriod = 0,
    postsInPeriodRows = [],
    previousPostsRows = [],
  } = input;

  if (!hasOverviewInsights(entities)) {
    return {
      growth: mock.growth,
      followerBalance: mock.followerBalance,
      viewedPosts: mock.viewedPosts,
    };
  }

  const days = daysInclusive(since, until);
  const fansMetric = pickMetric(entities, ["page_fans", "page_follows"]);
  const fanPts = sortedMetricPointsInRange(fansMetric, since, until);
  const followersTotal = lastMetricValueInRange(fansMetric, since, until);
  const previousFollowersTotal = lastMetricValueInRange(
    pickMetric(previousEntities, ["page_fans", "page_follows"]),
    previousSince,
    previousUntil
  );

  const viewsSum = sumMetricInRange(
    pickMetric(entities, ["page_impressions", "page_media_view"]),
    since,
    until
  );
  const previousViewsSum = sumMetricInRange(
    pickMetric(previousEntities, ["page_impressions", "page_media_view"]),
    previousSince,
    previousUntil
  );

  const pageViewsSum = sumMetricInRange(
    pickMetric(entities, ["page_views_total"]),
    since,
    until
  );
  const previousPageViewsSum = sumMetricInRange(
    pickMetric(previousEntities, ["page_views_total"]),
    previousSince,
    previousUntil
  );

  let acquired = 0;
  let lost = 0;
  for (let i = 1; i < fanPts.length; i += 1) {
    const delta = fanPts[i].v - fanPts[i - 1].v;
    if (delta > 0) acquired += delta;
    else lost += Math.abs(delta);
  }
  const netFollowers =
    fanPts.length >= 2 ? fanPts[fanPts.length - 1].v - fanPts[0].v : 0;

  const dailyNewFollowers = Math.round((netFollowers * 100) / days) / 100;
  const pageViewsPerDay = Math.round((pageViewsSum * 100) / days) / 100;
  const postsPerDay = Math.round((postsInPeriod * 100) / days) / 100;
  const postsPerWeek = Math.round(postsPerDay * 7 * 100) / 100;

  const currentPostViews = postsInPeriodRows.reduce(
    (a, p) => a + (p.views || 0),
    0
  );
  const currentPostReactions = postsInPeriodRows.reduce(
    (a, p) => a + (p.reactionCount || 0),
    0
  );
  const previousPostViews = previousPostsRows.reduce(
    (a, p) => a + (p.views || 0),
    0
  );
  const previousPostReactions = previousPostsRows.reduce(
    (a, p) => a + (p.reactionCount || 0),
    0
  );
  const viewedViews =
    currentPostViews > 0 ? currentPostViews : Math.round(viewsSum);
  const viewedReactions =
    currentPostReactions > 0
      ? currentPostReactions
      : Math.round(
          sumMetricInRange(
            pickMetric(entities, ["page_post_engagements"]),
            since,
            until
          )
        );

  const summary: FacebookStatCard[] = [
    withVariation(mock.growth.summary[0], Math.round(followersTotal), Math.round(previousFollowersTotal)),
    withVariation(mock.growth.summary[1], Math.round(viewsSum), Math.round(previousViewsSum)),
    withVariation(mock.growth.summary[2], Math.round(pageViewsSum), Math.round(previousPageViewsSum)),
    withVariation(mock.growth.summary[3], postsInPeriod, previousPostsInPeriod),
  ];

  const cadence: FacebookStatCard[] = [
    { ...mock.growth.cadence[0], value: dailyNewFollowers },
    { ...mock.growth.cadence[1], value: pageViewsPerDay },
    { ...mock.growth.cadence[2], value: postsPerDay },
    { ...mock.growth.cadence[3], value: postsPerWeek },
  ];

  const trend = buildOverviewTrend(entities, since, until, mock.growth.trend);

  const followerBalance: FacebookStatCard[] = [
    withVariation(mock.followerBalance[0], Math.round(netFollowers), 0),
    withVariation(mock.followerBalance[1], Math.round(acquired), 0),
    { ...mock.followerBalance[2], value: Math.round(lost) },
    withVariation(
      mock.followerBalance[3],
      postsInPeriod,
      previousPostsInPeriod
    ),
  ];

  const viewedPosts: FacebookStatCard[] = [
    withVariation(mock.viewedPosts[0], viewedViews, previousPostViews),
    withVariation(mock.viewedPosts[1], viewedReactions, previousPostReactions),
  ];

  return {
    growth: { summary, cadence, trend },
    followerBalance,
    viewedPosts,
  };
}

export function buildFacebookDemographicsAndClicks(
  entities: Record<string, Metric>,
  mock: FacebookAnalyticsSnapshot,
  since?: string,
  until?: string
): Pick<FacebookAnalyticsSnapshot, "demographics" | "clicks"> {
  const countryMetric = pickMetric(entities, [
    "page_fans_country",
    "page_follows_country",
  ]);
  const cityMetric = pickMetric(entities, ["page_fans_city", "page_follows_city"]);

  const demographics = hasLifetimeDemographics(entities)
    ? {
        countries: mapCountries(countryMetric, mock.demographics.countries),
        cities: mapCities(cityMetric, mock.demographics.cities),
      }
    : mock.demographics;

  if (!hasDailyClicks(entities)) {
    return { demographics, clicks: mock.clicks };
  }

  const totalActions = sumMetricInRange(
    pickMetric(entities, ["page_total_actions"]),
    since,
    until
  );
  const pageViews = sumMetricInRange(
    pickMetric(entities, ["page_views_total"]),
    since,
    until
  );
  const impressions = sumMetricInRange(
    pickMetric(entities, ["page_impressions", "page_media_view"]),
    since,
    until
  );

  const summary: FacebookStatCard[] = [
    {
      id: "totalClicks",
      title: "Clics totales",
      value: totalActions,
    },
    {
      id: "pageViews",
      title: "Visitas a página",
      value: pageViews,
    },
    {
      id: "content",
      title: "Visualizaciones",
      value: impressions,
    },
  ];

  const types: FacebookAudienceSegment[] = [
    { label: "Visualizaciones", value: impressions },
    { label: "Clics totales", value: totalActions },
    { label: "Visitas a página", value: pageViews },
  ];

  return {
    demographics,
    clicks: { summary, types },
  };
}

export const filterPostsInRange = (
  posts: FacebookPagePost[],
  since: string,
  until: string
): FacebookPagePost[] => {
  const start = parseRangeMoment(since).startOf("day");
  const end = parseRangeMoment(until).endOf("day");
  if (!start.isValid() || !end.isValid()) return posts;
  return posts.filter((p) => {
    const ts = Number(p.timestamp) || 0;
    if (!ts) return true;
    const t = moment.unix(ts);
    return t.isBetween(start, end, undefined, "[]");
  });
};

const aggregatePosts = (posts: FacebookPagePost[]) => {
  const reactions = posts.reduce((a, p) => a + (p.reactionCount || 0), 0);
  const comments = posts.reduce((a, p) => a + (p.commentCount || 0), 0);
  const shares = posts.reduce((a, p) => a + (p.shareCount || 0), 0);
  const clicks = posts.reduce((a, p) => a + (p.clicks || 0), 0);
  const views = posts.reduce((a, p) => a + (p.views || 0), 0);
  const reach = posts.reduce((a, p) => a + (p.reach || 0), 0);
  const interactions = reactions + comments + shares + clicks;
  return { reactions, comments, shares, clicks, views, reach, interactions };
};

const mapPostRow = (post: FacebookPagePost): FacebookPostMetric => {
  const ts = Number(post.timestamp) || 0;
  const m = ts > 0 ? moment.unix(ts).locale("es") : moment().locale("es");
  const caption = String(post.message || "").trim();
  return {
    id: post.id,
    caption: caption || "(Sin texto)",
    typeLabel: post.typeLabel || "Publicación",
    dateLabel: m.format("D MMM YYYY"),
    timeLabel: m.format("HH:mm"),
    reach: post.reach || 0,
    views: post.views || 0,
    reactions: post.reactionCount || 0,
    comments: post.commentCount || 0,
    shares: post.shareCount || 0,
    clicks: post.clicks || 0,
  };
};

const buildPublicationsTrend = (
  since: string,
  until: string,
  posts: FacebookPagePost[],
  baseTrend: FacebookTrendPoint[]
): FacebookTrendPoint[] => {
  const start = parseRangeMoment(since).startOf("day");
  const end = parseRangeMoment(until).startOf("day");
  if (!start.isValid() || !end.isValid()) return baseTrend;

  const byDay = new Map<
    string,
    { views: number; reactions: number; posts: number; clicks: number }
  >();
  for (const post of posts) {
    const ts = Number(post.timestamp) || 0;
    if (!ts) continue;
    const key = moment.unix(ts).format("YYYY-MM-DD");
    const row = byDay.get(key) || {
      views: 0,
      reactions: 0,
      posts: 0,
      clicks: 0,
    };
    row.views += post.views || 0;
    row.reactions += post.reactionCount || 0;
    row.clicks += post.clicks || 0;
    row.posts += 1;
    byDay.set(key, row);
  }

  const followersByDay = new Map<string, number>();
  for (const point of baseTrend) {
    const key = dateKey(point.label);
    if (key) followersByDay.set(key, point.followers);
  }

  const trend: FacebookTrendPoint[] = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    const day = byDay.get(key);
    const lastFollowers =
      Array.from(followersByDay.values()).pop() ??
      baseTrend[0]?.followers ??
      0;
    trend.push({
      label: formatAxisLabelEs(key),
      followers: followersByDay.get(key) ?? lastFollowers,
      views: day?.views ?? 0,
      pageViews: 0,
      posts: day?.posts ?? 0,
      reactions: day?.reactions ?? 0,
      clicks: day?.clicks ?? 0,
    });
    cursor.add(1, "day");
  }
  return trend.length ? trend : baseTrend;
};

export interface BuildFacebookPublicationsInput {
  mock: FacebookAnalyticsSnapshot;
  posts: FacebookPagePost[];
  previousPosts: FacebookPagePost[];
  since: string;
  until: string;
  previousSince: string;
  previousUntil: string;
  /** True after the publications API request has completed at least once. */
  postsLoaded?: boolean;
}

const emptyPublicationsFromMock = (
  mock: FacebookAnalyticsSnapshot,
  since: string,
  until: string
): Pick<FacebookAnalyticsSnapshot, "publications"> => {
  const zero = (item: FacebookStatCard): FacebookStatCard => ({
    ...item,
    value: item.unit === "percent" ? 0 : item.id === "posts" ? 0 : 0,
    direction: "flat",
    unit: item.unit,
  });
  return {
    publications: {
      summary: mock.publications.summary.map(zero),
      interactions: mock.publications.interactions.map((item) => ({
        ...zero(item),
        value:
          item.id === "comments" || item.id === "shares" ? "-" : 0,
      })),
      rates: mock.publications.rates.map((item) => ({
        ...zero(item),
        value: "-",
      })),
      organicPaid: [
        { label: "Orgánicas (Visualizaciones)", value: 0 },
        { label: "De Pago (Visualizaciones)", value: 0 },
      ],
      posts: [],
      chartTrend: buildPublicationsTrend(
        since,
        until,
        [],
        mock.publications.chartTrend
      ),
    },
  };
};

export function buildFacebookPublicationsAndTrend(
  input: BuildFacebookPublicationsInput
): Pick<FacebookAnalyticsSnapshot, "publications"> {
  const {
    mock,
    posts,
    previousPosts,
    since,
    until,
    previousSince,
    previousUntil,
    postsLoaded = false,
  } = input;

  const inRange = filterPostsInRange(posts, since, until);
  if (!inRange.length) {
    if (!postsLoaded) {
      return { publications: mock.publications };
    }
    return emptyPublicationsFromMock(mock, since, until);
  }

  const previousInRange = filterPostsInRange(
    previousPosts,
    previousSince,
    previousUntil
  );
  const current = aggregatePosts(inRange);
  const previous = aggregatePosts(previousInRange);
  const postsInPeriod = inRange.length;
  const previousPostsInPeriod = previousInRange.length;
  const days = daysInclusive(since, until);
  const previousDays = daysInclusive(previousSince, previousUntil);

  const avgReachPost =
    postsInPeriod > 0 ? Math.round(current.reach / postsInPeriod) : 0;
  const previousAvgReachPost =
    previousPostsInPeriod > 0
      ? Math.round(previous.reach / previousPostsInPeriod)
      : 0;
  const engagementPct =
    current.reach > 0
      ? Math.round((current.interactions * 1000) / current.reach) / 10
      : 0;
  const previousEngagementPct =
    previous.reach > 0
      ? Math.round((previous.interactions * 1000) / previous.reach) / 10
      : 0;

  const summary: FacebookStatCard[] = [
    withVariation(
      mock.publications.summary[0],
      engagementPct,
      previousEngagementPct
    ),
    withVariation(
      mock.publications.summary[1],
      current.interactions,
      previous.interactions
    ),
    withVariation(mock.publications.summary[2], avgReachPost, previousAvgReachPost),
    withVariation(mock.publications.summary[3], current.views, previous.views),
    withVariation(
      mock.publications.summary[4],
      postsInPeriod,
      previousPostsInPeriod
    ),
  ];
  summary[0] = { ...summary[0], unit: "percent" };

  const interactions: FacebookStatCard[] = [
    withVariation(
      mock.publications.interactions[0],
      current.reactions,
      previous.reactions
    ),
    withVariation(
      mock.publications.interactions[1],
      current.comments,
      previous.comments
    ),
    withVariation(
      mock.publications.interactions[2],
      current.shares,
      previous.shares
    ),
    withVariation(
      mock.publications.interactions[3],
      current.clicks,
      previous.clicks
    ),
    withVariation(
      mock.publications.interactions[4],
      postsInPeriod,
      previousPostsInPeriod
    ),
  ];

  const dailyReactions =
    Math.round((current.reactions * 100) / days) / 100;
  const reactionsPerPost =
    postsInPeriod > 0
      ? Math.round((current.reactions * 100) / postsInPeriod) / 100
      : 0;
  const dailyComments =
    Math.round((current.comments * 100) / days) / 100;
  const commentsPerPost =
    postsInPeriod > 0
      ? Math.round((current.comments * 100) / postsInPeriod) / 100
      : 0;
  const dailyShares = Math.round((current.shares * 100) / days) / 100;
  const sharesPerPost =
    postsInPeriod > 0
      ? Math.round((current.shares * 100) / postsInPeriod) / 100
      : 0;

  const previousDailyReactions =
    Math.round((previous.reactions * 100) / previousDays) / 100;
  const previousReactionsPerPost =
    previousPostsInPeriod > 0
      ? Math.round((previous.reactions * 100) / previousPostsInPeriod) / 100
      : 0;
  const previousDailyComments =
    Math.round((previous.comments * 100) / previousDays) / 100;
  const previousCommentsPerPost =
    previousPostsInPeriod > 0
      ? Math.round((previous.comments * 100) / previousPostsInPeriod) / 100
      : 0;
  const previousDailyShares =
    Math.round((previous.shares * 100) / previousDays) / 100;
  const previousSharesPerPost =
    previousPostsInPeriod > 0
      ? Math.round((previous.shares * 100) / previousPostsInPeriod) / 100
      : 0;

  const rates: FacebookStatCard[] = [
    withVariation(
      mock.publications.rates[0],
      dailyReactions,
      previousDailyReactions
    ),
    withVariation(
      mock.publications.rates[1],
      reactionsPerPost,
      previousReactionsPerPost
    ),
    {
      ...withVariation(
        mock.publications.rates[2],
        dailyComments,
        previousDailyComments
      ),
      value: dashOrNumber(current.comments > 0 ? dailyComments : 0),
    },
    {
      ...withVariation(
        mock.publications.rates[3],
        commentsPerPost,
        previousCommentsPerPost
      ),
      value: dashOrNumber(
        current.comments > 0 && postsInPeriod > 0 ? commentsPerPost : 0
      ),
    },
    {
      ...withVariation(
        mock.publications.rates[4],
        dailyShares,
        previousDailyShares
      ),
      value: dashOrNumber(current.shares > 0 ? dailyShares : 0),
    },
    {
      ...withVariation(
        mock.publications.rates[5],
        sharesPerPost,
        previousSharesPerPost
      ),
      value: dashOrNumber(
        current.shares > 0 && postsInPeriod > 0 ? sharesPerPost : 0
      ),
    },
  ];

  const organicPaid = [
    { label: "Orgánicas (Visualizaciones)", value: current.views },
    { label: "De Pago (Visualizaciones)", value: 0 },
  ];

  const tablePosts = [...inRange]
    .sort((a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0))
    .map(mapPostRow);

  const chartTrend = buildPublicationsTrend(
    since,
    until,
    inRange,
    mock.publications.chartTrend
  );

  return {
    publications: {
      summary,
      interactions,
      rates,
      organicPaid,
      posts: tablePosts,
      chartTrend,
    },
  };
}
